import { InteractionType } from '@lendi/core-constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { createContext, useEffect, useState } from 'react';
import { CalendarUrls, User, V3AvailabilityType } from '../api';
import { usePreselectedDateTimeQuery } from '../hooks';
import { Store, TeamMember } from '../interfaces';
import { Address } from '../pages/appointment-type';
import { AvailableAppointmentDate, AvailableAppointmentTimeString } from '../utils/helpers';

export type AppointmentDetailsContextType = {
  address?: Address;
  setAddress: (address: Address) => void;
  appointmentType: InteractionType;
  setAppointmentType: (appointmentType: InteractionType) => void;
  appointmentDate?: string;
  setAppointmentDate: (appointmentDate: string) => void;
  appointmentTime?: AvailableAppointmentDate;
  setAppointmentTime: (appointmentTime: AvailableAppointmentDate) => void;
  broker?: TeamMember;
  setBroker: (broker: TeamMember) => void;
  customer?: User;
  setCustomer: (customer: User) => void;
  store?: Store;
  setStore: React.Dispatch<React.SetStateAction<Store | undefined>>;
  teamRawAvailTimeslots?: AvailableAppointmentTimeString[];
  setTeamRawAvailTimeslots: (rawTimeSlots?: AvailableAppointmentTimeString[]) => void;
  appointmentId?: string;
  setAppointmentId: (appointmentId: string) => void;
  instructionNotes?: string;
  setInstructionNotes: (instructionNotes: string) => void;
  calendarUrls?: CalendarUrls;
  setCalendarUrls: (urls: CalendarUrls) => void;
  allAvailabilityTimes: Partial<V3AvailabilityType>;
  setAllAvailabilityTimes: (allAvailabilityTimes: Partial<V3AvailabilityType>) => void;
};

export const AppointmentDetailsContext = createContext<AppointmentDetailsContextType>(
  {} as AppointmentDetailsContextType
);

export const getDefaultAppointmentType = (
  absStoreAppointment: boolean,
  absVideoAppointment: boolean,
  absF2FAppointment: boolean,
  hasF2FAvailabilityTimes: boolean,
  hasVideoAvailabilityTimes: boolean,
  hasStoreAvailabilityTimes: boolean,
  hasPreselectedDateTime: boolean
): InteractionType => {
  if (hasPreselectedDateTime && absVideoAppointment && hasVideoAvailabilityTimes) {
    return InteractionType.Video;
  }
  if (absStoreAppointment && hasStoreAvailabilityTimes) {
    return InteractionType.Store;
  }
  if (absF2FAppointment && hasF2FAvailabilityTimes) {
    return InteractionType.FaceToFace;
  }
  if (absVideoAppointment && hasVideoAvailabilityTimes) {
    return InteractionType.Video;
  }
  return InteractionType.Phone;
};

export const AppointmentDetailsContextProvider: React.FC = ({ children }) => {
  const { absVideoAppointment, absF2FAppointment, absStoreAppointment } = useFlags();

  const [address, setAddress] = useState({});
  const [appointmentId, setAppointmentId] = useState<string>();
  const [instructionNotes, setInstructionNotes] = useState<string>();
  const [appointmentDate, setAppointmentDate] = useState<string>();
  const [appointmentTime, setAppointmentTime] = useState<AvailableAppointmentDate>();
  const [broker, setBroker] = useState<TeamMember>();
  const [customer, setCustomer] = useState<User>();
  const [store, setStore] = useState<Store>();
  const [teamRawAvailTimeslots, setTeamRawAvailTimeslots] = useState<AvailableAppointmentTimeString[]>();
  const [calendarUrls, setCalendarUrls] = useState<CalendarUrls>({});
  const [appointmentType, setAppointmentType] = useState(InteractionType.Phone);
  const [allAvailabilityTimes, setAllAvailabilityTimes] = useState<Partial<V3AvailabilityType>>({});
  const { preselectedDateTime } = usePreselectedDateTimeQuery();

  const hasStoreAvailabilityTimes = allAvailabilityTimes[InteractionType.Store]
    ? allAvailabilityTimes[InteractionType.Store]?.availability?.length !== 0
    : false;

  const hasF2FAvailabilityTimes = allAvailabilityTimes[InteractionType.FaceToFace]
    ? allAvailabilityTimes[InteractionType.FaceToFace]?.availability?.length !== 0
    : false;

  const hasVideoAvailabilityTimes = allAvailabilityTimes[InteractionType.Video]
    ? allAvailabilityTimes[InteractionType.Video]?.availability?.length !== 0
    : false;

  useEffect(() => {
    const defaultAppointmentType = getDefaultAppointmentType(
      absStoreAppointment,
      absVideoAppointment,
      absF2FAppointment,
      hasF2FAvailabilityTimes,
      hasVideoAvailabilityTimes,
      hasStoreAvailabilityTimes,
      !!preselectedDateTime
    );
    setAppointmentType(defaultAppointmentType);
  }, [
    absVideoAppointment,
    absF2FAppointment,
    hasF2FAvailabilityTimes,
    hasVideoAvailabilityTimes,
    hasStoreAvailabilityTimes,
    absStoreAppointment,
    preselectedDateTime,
  ]);

  return (
    <AppointmentDetailsContext.Provider
      value={{
        address,
        setAddress,
        appointmentType,
        setAppointmentType,
        appointmentDate,
        setAppointmentDate,
        appointmentTime,
        setAppointmentTime,
        broker,
        setBroker,
        customer,
        setCustomer,
        store,
        setStore,
        teamRawAvailTimeslots,
        setTeamRawAvailTimeslots,
        appointmentId,
        setAppointmentId,
        instructionNotes,
        setInstructionNotes,
        calendarUrls,
        setCalendarUrls,
        allAvailabilityTimes,
        setAllAvailabilityTimes,
      }}
    >
      {children}
    </AppointmentDetailsContext.Provider>
  );
};
